import Breadcrumbs from "@/components/Breadcrumbs";
import { AssetsService, SettingsService } from "@/services/admin-services";
import { SettingsDiscipline } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import { ArchiveBoxArrowDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    Alert,
    AlertColor,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import clsx from "clsx";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const breadcrumbs = [
    {
        to: "../../..",
        title: "Administration",
    },
    {
        title: "Settings",
        to: "../../..",
    },
    {
        title: "Disciplines",
        to: "..",
    },
];

const Details = () => {
    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);
    const [data, setData] = useState<SettingsDiscipline>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>();
    const [translations, setTranslations] = useState<Record<string, string>>({});
    const [actorTranslations, setActorTranslations] = useState<Record<string, string>>({});
    const [languages, setLanguages] = useState<Array<Language>>([]);
    const [order, setOrder] = useState<number>(0);

    const [file, setFile] = useState<File | null>(null);

    const [notification, setNotification] = useState<{
        open: boolean;
        message?: string;
        type: AlertColor;
    }>({ open: false, type: "success" });

    const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const settingsService = useMemo(() => new SettingsService("int"), []);
    const assetsService = useMemo(() => new AssetsService(), []);

    const navigate = useNavigate();
    const { identifier } = useParams();

    useEffect(() => {
        Promise.all([
            settingsService.getDisciplines().then((data) => {
                const discipline = data.find((x) => x.id === identifier);
                setData(discipline);
                setOrder(discipline?.order || 0);
            }),
            LocaleService.get().then(parseLanguages),
        ]).then(() => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!data) return;
        setBreadcrumbTrail([...breadcrumbs, { title: `${data.code}`, to: "" }]);
    }, [data]);

    useEffect(() => {
        if (
            !data ||
            !languages ||
            (translations && Object.keys(translations).length > 0) ||
            (actorTranslations && Object.keys(actorTranslations).length > 0)
        )
            return;

        const translationPerLanguage: Record<string, string> = {};
        for (let i = 0; i < languages.length; i++) {
            const language = languages[i];
            const translation = data.labels.find((x) => x.languageCode === language.slug);

            translationPerLanguage[language.slug] = translation?.label || "";
        }

        setTranslations(translationPerLanguage);

        const actorTranslationPerLanguage: Record<string, string> = {};
        for (let i = 0; i < languages.length; i++) {
            const language = languages[i];
            const translation = data.actors.find((x) => x.languageCode === language.slug);

            actorTranslationPerLanguage[language.slug] = translation?.label || "";
        }

        setActorTranslations(actorTranslationPerLanguage);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, languages]);

    useEffect(() => {
        if (!notification.open) return;

        notificationTimerRef.current = setTimeout(() => hideNotification(), 3000);
    }, [notification]);

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const displayNotification = (message: string, type: AlertColor) => setNotification({ open: true, message, type });
    const hideNotification = () => setNotification({ open: false, type: "success" });

    const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (!files) return;
        setFile(files[0]);
    };

    const onFileSelect = () => {
        if (!inputRef.current) return;
        inputRef.current.click();
    };

    const onCancel = () => navigate("..");

    const onSave = async () => {
        if (!data) return;

        setIsSaving(true);

        if (file) {
            const form = new FormData();
            form.append("Name", `discipline-icon-${data.id}`);
            form.append("File", file);

            const result = await assetsService.create(form);
            data.icon = result.file.url;
        }

        settingsService
            .updateDiscipline(data.id, {
                ...data,
                labels: Object.keys(translations).map((key) => ({ languageCode: key, label: translations[key] })),
                actors: Object.keys(actorTranslations).map((key) => ({
                    languageCode: key,
                    label: actorTranslations[key],
                })),
                order,
            })
            .then((result) => {
                setData({ ...result, code: data.code });
                displayNotification("Discipline updated successfully.", "success");
                setIsSaving(false);
            });
    };

    if (isLoading || !data)
        return (
            <Box>
                <Breadcrumbs trail={breadcrumbs} />

                <Divider />
                <Box m={4} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 500 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbTrail} />

            <Divider />

            <Box m={4} sx={{ display: "flex" }}>
                <Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
                    <Typography variant={"h4"}>{data.code}</Typography>
                    {/* <Typography variant={"subtitle2"}>{renderNames()}</Typography> */}
                </Box>
            </Box>

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Box sx={{ width: "100%" }}>
                    <Collapse in={notification.open}>
                        <Alert
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => hideNotification()}
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </IconButton>
                            }
                            severity={notification.type}
                        >
                            {notification.message}
                        </Alert>
                    </Collapse>
                </Box>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={700}>
                        Labels
                    </Typography>
                    <Typography variant="subtitle2" color={"text.secondary"}>
                        Configure translations.
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={4}>
                        {Object.keys(translations).length > 0 &&
                            languages
                                .sort((a, b) => a.label.localeCompare(b.label))
                                .map((language) => (
                                    <Grid size={{ xs: 12, sm: 6 }} key={language.slug}>
                                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                            {language.label}
                                        </Typography>
                                        <TextField
                                            label="Translation"
                                            variant="outlined"
                                            fullWidth
                                            value={translations[language.slug]}
                                            disabled={isSaving}
                                            onChange={(e) =>
                                                setTranslations({
                                                    ...translations,
                                                    [language.slug]: e.currentTarget.value,
                                                })
                                            }
                                        />
                                    </Grid>
                                ))}
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={700}>
                        Actors
                    </Typography>
                    <Typography variant="subtitle2" color={"text.secondary"}>
                        Configure actor translations.
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={4}>
                        {Object.keys(actorTranslations).length > 0 &&
                            languages
                                .sort((a, b) => a.label.localeCompare(b.label))
                                .map((language) => (
                                    <Grid size={{ xs: 12, sm: 6 }} key={language.slug}>
                                        <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                            {language.label}
                                        </Typography>
                                        <TextField
                                            label="Translation"
                                            variant="outlined"
                                            fullWidth
                                            value={actorTranslations[language.slug]}
                                            disabled={isSaving}
                                            onChange={(e) =>
                                                setActorTranslations({
                                                    ...actorTranslations,
                                                    [language.slug]: e.currentTarget.value,
                                                })
                                            }
                                        />
                                    </Grid>
                                ))}
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={700}>
                        Settings
                    </Typography>
                    <Typography variant="subtitle2" color={"text.secondary"}>
                        Additional configuration.
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                Order
                            </Typography>
                            <TextField
                                label="Order"
                                type="number"
                                variant="outlined"
                                fullWidth
                                value={order}
                                disabled={isSaving}
                                onChange={(e) => setOrder(parseInt(e.currentTarget.value))}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12 }}>
                            <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                Icon
                            </Typography>
                            <input
                                value=""
                                style={{ visibility: "hidden", opacity: 0, height: 0, width: 0 }}
                                ref={inputRef}
                                type="file"
                                accept="image/*,video/*"
                                onChange={onFileInputChange}
                            />
                            <div className="flex items-center gap-4">
                                {file || data.icon ? (
                                    <img
                                        src={file ? URL.createObjectURL(file) : data.icon}
                                        alt="Discipline Icon"
                                        className={clsx(["h-[52px]", "flex-shrink-0"])}
                                    />
                                ) : (
                                    <span>No icon available</span>
                                )}
                                <Button variant="outlined" onClick={onFileSelect}>
                                    Change icon
                                </Button>
                            </div>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ display: "flex", alignContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{ ml: "auto" }}
                                onClick={() => onCancel()}
                                disabled={Boolean(isSaving)}
                            >
                                Cancel
                            </Button>
                            <Box sx={{ ml: 1, position: "relative" }}>
                                <Button
                                    variant="contained"
                                    startIcon={<ArchiveBoxArrowDownIcon className="h-5 w-5" />}
                                    sx={{ ml: 1 }}
                                    onClick={() => onSave()}
                                    disabled={Boolean(isSaving)}
                                >
                                    Save
                                </Button>
                                {isSaving && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Details;
